/**
 * @copyright Copyright 2024-2024 Epic Systems Corporation
 * @file Review questionnaire attestation.
 * @module Epic.AppOrchard.Core.Questionnaire.Attestation
 */

import {
	Box,
	Button,
	ButtonGroup,
	Divider,
	FormControl,
	FormLabel,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Radio,
	RadioGroup,
	Stack,
	Text,
	useToast,
} from "@chakra-ui/react";
import { useAsync } from "@epic/react-async-hook";
import { config } from "ao/appConfig";
import useQuestionnaire from "ao/components/Questionnaire/UseQuestionnaire";
import { AcknowledgementChoice, IAttestationModel } from "ao/data";
import { isInFlight } from "ao/utils/useAsyncHelpers";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { BiSave } from "react-icons/bi";
import { buildAcknowledgementText, saveAttestationAcknowledgement } from "./data/data";

interface IProps {
	attestation: IAttestationModel;
	appId: number;
	isVisible: boolean;
	includeNewVersion?: boolean;
	headerButtons?: React.ReactNode;
	footerButtons?: React.ReactNode;
	onComplete: (
		accurateForCurrentVersion: boolean,
		accurateForNewVersion: boolean,
		currentVersionAcknowledgement: string,
	) => void;
	onCancel?: () => void;
}

export const ReviewAttestationModal: React.FunctionComponent<IProps> = memo((props: IProps) => {
	const {
		attestation,
		appId,
		isVisible,
		includeNewVersion,
		headerButtons,
		footerButtons,
		onComplete,
		onCancel,
	} = props;

	const toast = useToast();

	const isInaccurate = attestation?.catalogIsInaccurate;

	const questionnaireVersion = attestation?.version ?? -1;

	const categories = attestation?.categories ?? [];
	const { buildSaveModel } = useQuestionnaire(categories, questionnaireVersion, true, appId);

	const [acknowledgementText, setAcknowledgementText] = useState<string>("");
	const [acknowledgementTextHasLoaded, setAcknowledgementTextHasLoaded] = useState<boolean>(false);

	const anyAcknowledgements = attestation?.acknowledgements && attestation.acknowledgements.length > 0;
	const notAccurateAcknowledgements = anyAcknowledgements
		? attestation.acknowledgements.filter((acknowledgement) => !acknowledgement.accurate)
		: [];
	const lastNotAccurateAcknowledgement =
		notAccurateAcknowledgements.length > 0
			? notAccurateAcknowledgements[notAccurateAcknowledgements.length - 1]
			: null;

	const controlHeaderText = isInaccurate
		? (lastNotAccurateAcknowledgement
				? `User ${
						lastNotAccurateAcknowledgement.userName
				  } determined the questionnaire is not currently accurate on ${new Date(
						lastNotAccurateAcknowledgement.date,
				  ).toLocaleDateString()}.`
				: "A previous user determined the questionnaire is not currently inaccurate.") +
		  (includeNewVersion ? " Will the above questionnaire be accurate in the new app version?" : "")
		: "Is the above questionnaire still accurate?";

	const handleOnComplete = useCallback(
		(isAccurate: boolean, isNextVersionAccurate: boolean) => {
			onComplete(isAccurate, isNextVersionAccurate, acknowledgementText);
		},
		[acknowledgementText, onComplete],
	);

	const [savingAttestationState, saveAttestation] = useAsync(saveAttestationAcknowledgement, {
		executeImmediately: false,
		displayName: "saveAttestationAcknowledgement",
		onSuccess: (response, extras) => {
			if (response.success) {
				const requestModel = extras.params[0];
				handleOnComplete(!requestModel.notAccurate, !requestModel.notAccurateForNextVersion);
			} else {
				toast({
					title: "Error",
					description:
						"Something went wrong when saving your responses. Refresh the page and try again.",
					status: "error",
					duration: config.ToastDuration,
					isClosable: true,
				});
			}
		},
		onFailure: () => {
			toast({
				title: "Error",
				description:
					"Something went wrong when saving your responses. Refresh the page and try again.",
				status: "error",
				duration: config.ToastDuration,
				isClosable: true,
			});
		},
	});

	const [buildAcknowledgementTextState, buildAcknowledgementTextHandler] = useAsync(
		buildAcknowledgementText,
		{
			executeImmediately: false,
			displayName: "buildAcknowledgementText",
			onSuccess: (response) => {
				if (response.success) {
					setAcknowledgementText(response.text);
				} else {
					toast({
						title: "Error",
						description:
							"Something went wrong when summarizing your answers. Refresh the page and try again.",
						status: "error",
						duration: config.ToastDuration,
						isClosable: true,
					});
				}
			},
			onFailure: () => {
				toast({
					title: "Error",
					description:
						"Something went wrong when summarizing your answers. Refresh the page and try again.",
					status: "error",
					duration: config.ToastDuration,
					isClosable: true,
				});
			},
		},
	);

	const acknowledgementChoiceOptions = useMemo(
		() =>
			includeNewVersion && !isInaccurate
				? [
						AcknowledgementChoice.AccurateInTheCurrentVersionAndWillBeAccurateInTheNewVersion,
						AcknowledgementChoice.NotAccurateInTheCurrentVersionButWillBeAccurateInTheNewVersion,
						AcknowledgementChoice.NotAccurateInTheCurrentVersionAndWillNotBeAccurateInTheNewVersion,
						AcknowledgementChoice.AccurateInTheCurrentVersionButWillNotBeAccurateInTheNewVersion,
				  ]
				: includeNewVersion && isInaccurate
				? [
						AcknowledgementChoice.NotAccurateInTheCurrentVersionButWillBeAccurateInTheNewVersion,
						AcknowledgementChoice.NotAccurateInTheCurrentVersionAndWillNotBeAccurateInTheNewVersion,
				  ]
				: [AcknowledgementChoice.Accurate, AcknowledgementChoice.NotAccurate],
		[includeNewVersion, isInaccurate],
	);
	const [acknowledgementChoice, setAcknowledgementChoice] = useState<AcknowledgementChoice>(0);

	useEffect(() => {
		if (isVisible && !acknowledgementTextHasLoaded) {
			setAcknowledgementTextHasLoaded(true);
			buildAcknowledgementTextHandler({ saveRequest: buildSaveModel(), testProcessHooks: false });
		}
	}, [acknowledgementTextHasLoaded, buildAcknowledgementTextHandler, buildSaveModel, isVisible]);

	//#region saving data
	const isSaving = isInFlight(savingAttestationState);
	const isBuildingAcknowledgementText = isInFlight(buildAcknowledgementTextState);
	//#endregion

	const handleCancel = useCallback(() => {
		setAcknowledgementChoice(0);
		if (onCancel) {
			onCancel();
		}
	}, [onCancel]);

	return (
		<Modal
			isOpen={isVisible && !!acknowledgementText}
			onClose={() => {
				if (onCancel) onCancel();
			}}
			size="3xl"
		>
			<ModalOverlay />
			<ModalContent>
				<>
					<ModalHeader>API Questionnaire</ModalHeader>
					{onCancel && <ModalCloseButton onClick={handleCancel} />}
					<ModalBody>
						{headerButtons && (
							<Box pos="absolute" top="4" right="3">
								{headerButtons}
							</Box>
						)}
						{attestation && <Text dangerouslySetInnerHTML={{ __html: attestation.headerText }} />}
						<Divider m="20px 0 15px" />
						<Box
							maxH={{ base: "50vh", md: "60vh" }}
							overflowY="auto"
							style={{ scrollbarGutter: "stable" }}
							px="3px"
						>
							<Text
								whiteSpace="pre-line"
								dangerouslySetInnerHTML={{ __html: acknowledgementText }}
							/>
						</Box>
						<Divider m="20px 0 15px" />
						{!includeNewVersion && <Text>{controlHeaderText}</Text>}
						{includeNewVersion && (
							<FormControl>
								<FormLabel>{controlHeaderText}</FormLabel>
								<RadioGroup
									onChange={(newValue) => {
										const parsedAcknowledgementChoice = parseInt(newValue);
										setAcknowledgementChoice(parsedAcknowledgementChoice);
									}}
									value={acknowledgementChoice}
								>
									<Stack direction="column">
										{acknowledgementChoiceOptions.map((choiceOption) => (
											<Radio key={choiceOption} value={choiceOption}>
												{choiceOption ===
												AcknowledgementChoice.AccurateInTheCurrentVersionAndWillBeAccurateInTheNewVersion
													? "Accurate in the current version and will be accurate in the new version"
													: choiceOption ===
													  AcknowledgementChoice.NotAccurateInTheCurrentVersionButWillBeAccurateInTheNewVersion
													? isInaccurate
														? "Will be accurate in the new version"
														: "Not accurate in the current version but will be accurate in the new version"
													: choiceOption ===
													  AcknowledgementChoice.NotAccurateInTheCurrentVersionAndWillNotBeAccurateInTheNewVersion
													? isInaccurate
														? "Will not be accurate in the new version"
														: "Not accurate in the current version and will not be accurate in the new version"
													: choiceOption ===
													  AcknowledgementChoice.AccurateInTheCurrentVersionButWillNotBeAccurateInTheNewVersion
													? "Accurate in the current version but will not be accurate in the new version"
													: "Error"}
											</Radio>
										))}
									</Stack>
								</RadioGroup>
							</FormControl>
						)}
					</ModalBody>
					<ModalFooter>
						<ButtonGroup>
							{includeNewVersion ? (
								<>
									<Button
										colorScheme="blue"
										type="button"
										disabled={
											isSaving ||
											isBuildingAcknowledgementText ||
											!acknowledgementChoice
										}
										title="Save your selection"
										leftIcon={<BiSave />}
										onClick={() => {
											const currentVersionAccurate =
												acknowledgementChoice === AcknowledgementChoice.Accurate ||
												acknowledgementChoice ===
													AcknowledgementChoice.AccurateInTheCurrentVersionAndWillBeAccurateInTheNewVersion ||
												acknowledgementChoice ===
													AcknowledgementChoice.AccurateInTheCurrentVersionButWillNotBeAccurateInTheNewVersion;
											const newVersionAccurate =
												!includeNewVersion ||
												acknowledgementChoice ===
													AcknowledgementChoice.AccurateInTheCurrentVersionAndWillBeAccurateInTheNewVersion ||
												acknowledgementChoice ===
													AcknowledgementChoice.NotAccurateInTheCurrentVersionButWillBeAccurateInTheNewVersion;

											saveAttestation({
												appId,
												questionnaireVersionId: questionnaireVersion,
												notAccurate: !currentVersionAccurate,
												notAccurateForNextVersion: !newVersionAccurate,
											});
										}}
									>
										Proceed
									</Button>
								</>
							) : (
								<>
									{!isInaccurate && (
										<Button
											colorScheme="blue"
											type="button"
											disabled={isSaving || isBuildingAcknowledgementText}
											onClick={() => {
												saveAttestation({
													appId,
													questionnaireVersionId: questionnaireVersion,
													notAccurate: false,
													notAccurateForNextVersion: false,
												});
											}}
											title="Save that the questionnaire is still accurate"
											leftIcon={<BiSave />}
										>
											Yes
										</Button>
									)}
									<Button
										colorScheme="blue"
										type="button"
										disabled={isSaving || isBuildingAcknowledgementText}
										onClick={() => {
											saveAttestation({
												appId,
												questionnaireVersionId: questionnaireVersion,
												notAccurate: true,
												notAccurateForNextVersion: false,
											});
										}}
										title="Save that the questionnaire is not currently accurate"
										leftIcon={<BiSave />}
									>
										No
									</Button>
								</>
							)}
							{footerButtons}
							{onCancel && (
								<Button type="button" disabled={isSaving} onClick={handleCancel}>
									Cancel
								</Button>
							)}
						</ButtonGroup>
					</ModalFooter>
				</>
			</ModalContent>
		</Modal>
	);
});
